import { parseJsonText, stringifyJson } from './json';

/**
 * Row shape returned from intake_form_definitions PowerSync table.
 * Column names match the PowerSync SQLite schema.
 */
export interface IntakeFormDefinitionRow {
  id: string;
  name: string | null;
  status: string | null;
  version: number | null;
  schema: string | null;
  validation_schema: string | null;
}

/**
 * View model with parsed schema/validation objects.
 * `schema` is typed as Record<string, unknown> because @helios/data-access
 * cannot depend on @helios/ui. The consumer casts to IntakeQuestionnaire.
 */
export interface IntakeFormDefinitionVM {
  id: string;
  name: string;
  status: 'draft' | 'published' | 'archived';
  version: number;
  schema: Record<string, unknown>;
  validationSchema: Record<string, unknown> | null;
}

const EMPTY_SCHEMA: Record<string, unknown> = {
  id: '',
  status: 'draft',
  version: 1,
  sections: [],
  lastModified: '',
};

/**
 * Transform a raw DB row into a typed view model with parsed JSON fields.
 */
export function buildIntakeDefinitionVM(row: IntakeFormDefinitionRow): IntakeFormDefinitionVM {
  return {
    id: row.id,
    name: row.name ?? 'Untitled',
    status: (row.status as IntakeFormDefinitionVM['status']) ?? 'draft',
    version: row.version ?? 1,
    schema: parseJsonText<Record<string, unknown>>(row.schema, { ...EMPTY_SCHEMA, id: row.id }),
    validationSchema: parseJsonText<Record<string, unknown> | null>(row.validation_schema, null),
  };
}

/**
 * Serialize fields for db.execute() writes.
 * Returns an object with column values ready for SQL parameter binding.
 */
export function serializeIntakeDefinitionForWrite(
  id: string,
  name: string,
  status: string,
  version: number,
  schema: Record<string, unknown>,
  validationSchema?: Record<string, unknown> | null,
): {
  id: string;
  name: string;
  status: string;
  version: number;
  schema: string;
  validation_schema: string | null;
} {
  return {
    id,
    name,
    status,
    version,
    schema: stringifyJson(schema),
    validation_schema: validationSchema ? stringifyJson(validationSchema) : null,
  };
}
